import { useGameStore } from './gameStore.js';
import { GRID_COLS, GRID_ROWS, GEM_COSTS } from './constants.js';
import { randomInt, areAdjacent } from './utils.js';

export class AttractMode {
  constructor(store, soundController, gameController) {
    this.store = store;
    this.soundController = soundController;
    this.gameController = gameController;
    this.idleTimer = 0;
    this.attractTimer = 0;
    this.botInterval = null;
    this.idleInterval = null;
    this.originalMuted = false;
    this.botActive = false;
    this.lastActionTime = 0;
    this.botIsDragging = false;
    this.botPendingTimeout = null;
    
    this.setupEventListeners();
  }
  
  setupEventListeners() {
    // Any input resets idle timer or interrupts attract mode
    const handleInput = () => {
      if (this.botActive) {
        // Player interrupted attract mode
        this.stopAttractMode(true);
        // Keep current game state, just stop bot
        return;
      }
      if (this.store.getState().phase === 'title') {
        this.idleTimer = 0;
      }
    };
    document.addEventListener('keydown', handleInput);
    document.addEventListener('pointerdown', handleInput);
    document.addEventListener('click', handleInput);
    document.addEventListener('mousedown', handleInput);
    document.addEventListener('touchstart', handleInput);
  }
  
  startIdleMonitoring() {
    if (this.idleInterval) clearInterval(this.idleInterval);
    
    this.idleInterval = setInterval(() => {
      const state = this.store.getState();
      if (state.phase === 'title' && !this.botActive) {
        this.idleTimer += 1;
        
        // 45 seconds idle triggers attract mode
        if (this.idleTimer >= 45) {
          this.startAttractMode();
        }
      } else {
        // Not on title screen, reset idle timer
        this.idleTimer = 0;
      }
    }, 1000);
  }
  
  startAttractMode() {
    console.log('Starting attract mode');
    const state = this.store.getState();
    if (state.phase !== 'title') return;
    
    // Save original mute state
    this.originalMuted = this.soundController ? this.soundController.audio.muted : false;
    
    // Auto-mute audio
    if (this.soundController) {
      this.soundController.audio.setMute(true);
      this.soundController.updateMuteButton();
    }
    
    // Start a new game with Polished difficulty (regardless of player's selection)
    // This is required by the acceptance criteria
    this.store.getState().initGame('attract_seed_123', 'polished');
    
    this.botActive = true;
    this.attractTimer = 0;
    this.lastActionTime = Date.now();
    
    // Start attract timer (max 60 seconds)
    const attractDuration = 60;
    const timerInterval = setInterval(() => {
      this.attractTimer += 1;
      
      // Check if bot died (game over or victory)
      const currentState = this.store.getState();
      if (currentState.phase !== 'playing' || this.attractTimer >= attractDuration) {
        this.stopAttractMode(false);
        clearInterval(timerInterval);
      }
    }, 1000);
    
    // Start bot action loop
    this.startBotLoop();
  }
  
  stopAttractMode(interrupted = false) {
    console.log('Stopping attract mode', interrupted ? '(interrupted)' : '');
    if (this.botInterval) {
      clearInterval(this.botInterval);
      this.botInterval = null;
    }
    if (this.botPendingTimeout) {
      clearTimeout(this.botPendingTimeout);
      this.botPendingTimeout = null;
    }
    
    // Restore original mute state
    if (this.soundController) {
      this.soundController.audio.setMute(this.originalMuted);
      this.soundController.updateMuteButton();
    }
    
    if (!interrupted) {
      // Return to title screen (bot died or timer expired)
      this.store.setState({ phase: 'title' });
    }
    // If interrupted, keep current game phase (player takes over)
    
    this.botActive = false;
    this.botIsDragging = false;
    this.idleTimer = 0;
    this.attractTimer = 0;
  }
  
  startBotLoop() {
    if (this.botInterval) clearInterval(this.botInterval);
    
    // Bot reaction delay ~300ms, with some variance
    this.botInterval = setInterval(() => {
      if (!this.botActive) return;
      
      const state = this.store.getState();
      if (state.phase !== 'playing') {
        this.stopAttractMode();
        return;
      }
      
      this.botAction();
    }, 300 + Math.random() * 200); // 300-500ms
  }
  
  botAction() {
    if (this.botIsDragging) return;
    
    const state = this.store.getState();
    const rng = state.rng;
    
    // Decide action: 70% try to merge, 30% try to purchase if mana available
    const rand = Math.random();
    
    if (rand < 0.7) {
      this.botMergeAction();
    } else {
      this.botPurchaseAction();
    }
  }
  
  botMergeAction() {
    const state = this.store.getState();
    const grid = state.grid;
    const rng = state.rng;
    
    // Collect all possible merges
    const merges = [];
    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        const gem = grid[row][col];
        if (!gem) continue;
        
        // Check adjacent cells
        const neighbors = [
          { row: row-1, col },
          { row: row+1, col },
          { row, col: col-1 },
          { row, col: col+1 }
        ];
        
        for (const nb of neighbors) {
          if (nb.row >= 0 && nb.row < GRID_ROWS && nb.col >= 0 && nb.col < GRID_COLS) {
            const targetGem = grid[nb.row][nb.col];
            if (targetGem && gem.type === targetGem.type && gem.tier === targetGem.tier) {
              merges.push({
                from: { row, col },
                to: { row: nb.row, col: nb.col }
              });
            }
          }
        }
      }
    }
    
    // 15% miss rate: pick invalid merge
    if (Math.random() < 0.15 || merges.length === 0) {
      // Pick random gem and random adjacent cell (may be empty or different)
      const allGems = [];
      for (let row = 0; row < GRID_ROWS; row++) {
        for (let col = 0; col < GRID_COLS; col++) {
          if (grid[row][col]) allGems.push({ row, col });
        }
      }
      if (allGems.length === 0) return;
      
      const from = allGems[randomInt(0, allGems.length, rng)];
      const directions = [
        { row: from.row-1, col: from.col },
        { row: from.row+1, col: from.col },
        { row: from.row, col: from.col-1 },
        { row: from.row, col: from.col+1 }
      ].filter(pos => pos.row >= 0 && pos.row < GRID_ROWS && pos.col >= 0 && pos.col < GRID_COLS);
      
      if (directions.length === 0) return;
      const to = directions[randomInt(0, directions.length, rng)];
      
      // Perform invalid merge (will snap back)
      this.simulateDragAndDrop(from, to);
    } else {
      // Pick a random valid merge
      const merge = merges[randomInt(0, merges.length, rng)];
      this.simulateDragAndDrop(merge.from, merge.to);
    }
  }
  
  botPurchaseAction() {
    const state = this.store.getState();
    if (state.mana < GEM_COSTS[1]) return;
    
    // Check if there's empty cell
    let emptyExists = false;
    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        if (!state.grid[row][col]) {
          emptyExists = true;
          break;
        }
      }
    }
    if (!emptyExists) return;
    
    // Purchase tier-1 gem
    state.purchaseGem(1);
  }
  
  simulateDragAndDrop(from, to) {
    this.botIsDragging = true;
    
    if (this.gameController && this.gameController.simulateDrag) {
      this.gameController.simulateDrag(from, to);
    } else {
      // Fallback: direct store manipulation (no visual drag)
      const state = this.store.getState();
      state.startDrag(from.row, from.col);
      state.dropGem(to.row, to.col);
    }
    
    // Allow next action after drag completes (approx 300ms)
    if (this.botPendingTimeout) clearTimeout(this.botPendingTimeout);
    this.botPendingTimeout = setTimeout(() => {
      this.botIsDragging = false;
    }, 300);
  }
}